/**
 * CreditsBalancePill.js — RC 1.38.B.1 Commercial Transparency (corrected).
 *
 * Persistent top-bar ledger. Reads from the RC 1.33.B+ usage model
 * (`/api/account/plan` + `/api/jobs/analyze-fit/usage`) — never
 * from the legacy `/api/credits/me` ledger.
 *
 * Per-plan layout (mobile-first):
 *
 *   Base   →  "Free fits today: 1/2 · Top-up: 0"
 *   Focus  →  "Plan: Focus · Included: 24 · Top-up: 0"
 *   Flow   →  "Plan: Flow · Included: 0 · Top-up: 0"
 *
 * Click → `/account?tab=plan`. Hard rails: zero schema, zero Stripe,
 * zero pricing change.
 */

import { Link } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCredits, formatBalance, isPaidPlan } from '@/lib/creditsContext';

function planLabel(code) {
  if (!code || typeof code !== 'string') return null;
  return code.charAt(0).toUpperCase() + code.slice(1);
}

export default function CreditsBalancePill() {
  const {
    planCode, includedUsageRemaining, extraUsageRemaining,
    freeFit, loading,
  } = useCredits();

  // Bootstrap window — render a low-cost skeleton so the testid is
  // always observable and the layout doesn't shift when the plan
  // signal arrives. Per RC 1.38.C.1 mobile-density follow-up.
  if (planCode == null) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-ndor-edge bg-ndor-surface-alt/40 text-[11px] whitespace-nowrap"
        data-testid="credits-balance-pill"
        data-state={loading ? 'loading' : 'empty'}
        aria-label="Plan and usage ledger (loading)"
      >
        <Wallet className="h-3.5 w-3.5 text-ndor-ink-muted shrink-0" aria-hidden />
        <span
          className="font-mono-ui text-ndor-ink-muted"
          data-testid="credits-pill-skeleton"
        >
          …
        </span>
      </span>
    );
  }

  const isPaid    = isPaidPlan(planCode);
  const planText  = planLabel(planCode);
  const inclText  = formatBalance(includedUsageRemaining);
  const extraText = formatBalance(extraUsageRemaining);

  // RC 1.49.x.WF — operator directive 2026-05-06: show TOTAL credits
  // (included + extra) in the pill instead of the split. The split
  // was clipping on Fold/tablet and was redundant with the
  // breakdown tooltip + the /credits page.
  const totalRemaining =
    (typeof includedUsageRemaining === 'number' ? includedUsageRemaining : 0) +
    (typeof extraUsageRemaining    === 'number' ? extraUsageRemaining    : 0);
  const totalText = formatBalance(totalRemaining);

  const freeRemaining = typeof freeFit?.remaining === 'number' ? freeFit.remaining : null;
  const freeLimit     = typeof freeFit?.limit === 'number' ? freeFit.limit : null;
  const showFree      = !isPaid && freeRemaining != null && freeLimit != null;

  // ── Tooltip body — same data, expanded ──
  const tooltipBody = (
    <>
      <p className="text-ndor-ink font-medium mb-1">Account ledger</p>
      <p className="text-ndor-ink-secondary leading-relaxed">
        {planText && (<>Plan: <span className="text-ndor-ink">{planText}</span><br/></>)}
        {isPaid ? (
          <>
            Total Credits: <span className="text-ndor-ink">{totalText}</span><br/>
            <span className="text-ndor-ink-muted text-[11px]">
              ({inclText} plan + {extraText} top-up)
            </span>
          </>
        ) : (
          <>
            Free Analyse Fits today:{' '}
            <span className="text-ndor-ink">
              {freeRemaining != null ? `${freeRemaining}/${freeLimit}` : '—'}
            </span>
            <br/>
            Top-up Credits: <span className="text-ndor-ink">{extraText}</span>
          </>
        )}
      </p>
      <p className="text-ndor-ink-muted mt-1.5 text-[10px]">
        Click to manage your plan and top up.
      </p>
    </>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to="/account?tab=plan"
          className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-2.5 py-1 rounded-md border border-ndor-edge bg-ndor-surface-alt/60 hover:bg-ndor-surface-alt hover:border-ndor-gold/40 transition-colors text-[11px] whitespace-nowrap max-w-full"
          data-testid="credits-balance-pill"
          data-plan-code={planCode || ''}
          data-included-remaining={typeof includedUsageRemaining === 'number' ? String(includedUsageRemaining) : ''}
          data-extra-remaining={typeof extraUsageRemaining === 'number' ? String(extraUsageRemaining) : ''}
          aria-label="Plan and usage ledger"
        >
          <Wallet className="h-3.5 w-3.5 text-ndor-gold shrink-0" aria-hidden />

          {planText && (
            <span className="hidden sm:inline-flex items-baseline gap-1">
              <span className="font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted text-[10px]">
                Plan
              </span>
              <span
                className="text-ndor-ink font-medium"
                data-testid="credits-pill-plan"
              >
                {planText}
              </span>
              <span className="text-ndor-ink-muted">·</span>
            </span>
          )}

          {isPaid ? (
            <span className="inline-flex items-baseline gap-1">
              <span className="font-mono-ui uppercase tracking-[0.14em] sm:tracking-[0.16em] text-ndor-ink-muted text-[10px]">
                Credits
              </span>
              <span
                className="text-ndor-ink font-mono-ui font-semibold"
                data-testid="credits-pill-total"
              >
                {totalText}
              </span>
            </span>
          ) : (
            <>
              {showFree && (
                <span className="inline-flex items-baseline gap-1">
                  <span className="font-mono-ui uppercase tracking-[0.14em] sm:tracking-[0.16em] text-ndor-ink-muted text-[10px]">
                    <span className="hidden sm:inline">Free fits today</span>
                    <span className="sm:hidden">Free</span>
                  </span>
                  <span
                    className={
                      freeRemaining > 0
                        ? 'text-ndor-success font-mono-ui font-semibold'
                        : 'text-ndor-ink-muted font-mono-ui'
                    }
                    data-testid="credits-pill-free-remaining"
                  >
                    {freeRemaining}/{freeLimit}
                  </span>
                </span>
              )}
              <span className="hidden md:inline-flex items-baseline gap-1 pl-2 ml-1 border-l border-ndor-edge">
                <span className="font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted text-[10px]">
                  Credits left
                </span>
                <span
                  className="text-ndor-ink font-mono-ui font-semibold"
                  data-testid="credits-pill-extra"
                >
                  {extraText}
                </span>
              </span>
            </>
          )}
        </Link>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="bg-ndor-surface border border-ndor-edge text-xs max-w-[280px]">
        {tooltipBody}
      </TooltipContent>
    </Tooltip>
  );
}
